import clsx from 'clsx';
import type { ReactNode } from 'react';
import { addDays } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useMoney, useNumber } from './kit';

/**
 * Small helpers the Overview, Financial Intelligence, AI Insights and Executive Reports pages share: links
 * that keep the period, a company day written in the reader's language, a period's range, and amounts
 * kept in their own currencies — never added across them here.
 */

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' } as const;

/** A path with the period's querystring kept, and any other parameter set (or removed with null). */
export function hrefWith(path: string, query: string, extra?: Record<string, string | null | undefined>): string {
  const q = new URLSearchParams(query);
  for (const [k, v] of Object.entries(extra ?? {})) {
    if (v) q.set(k, v);
    else q.delete(k);
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

/** The last day of a period the API sends with an exclusive end. */
export function lastDayOf(endExclusive: string): string {
  return addDays(endExclusive, -1);
}

/**
 * The period query that reads a stored report's period live, as the period picker writes it. A week is
 * asked as its seven days: the picker has no week of its own beside "this week" and "last week".
 */
export function periodQueryOf(kind: string, start: string, endExclusive: string): string {
  const q = new URLSearchParams();
  if (kind === 'DAILY') {
    q.set('period', 'DAILY');
    q.set('date', start);
  } else if (kind === 'MONTHLY') {
    q.set('period', 'MONTHLY');
    q.set('month', start.slice(0, 7));
  } else {
    q.set('period', 'CUSTOM');
    q.set('from', start);
    q.set('to', lastDayOf(endExclusive));
  }
  return q.toString();
}

/** A company day (`YYYY-MM-DD`) in the reader's language — read as that calendar day, never shifted by a time zone. */
export function useDay() {
  const { locale } = useI18n();
  return (day: string | null | undefined) => {
    if (!day) return '—';
    const d = new Date(`${day.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return day;
    return new Intl.DateTimeFormat(LOCALE_TAG[locale], { dateStyle: 'medium', timeZone: 'UTC' }).format(d);
  };
}

/** A month (`YYYY-MM-01` or `YYYY-MM`) as "November 2026", or "Nov 26" short. */
export function useMonthName() {
  const { locale } = useI18n();
  return (month: string, short = false) => {
    const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return month;
    return new Intl.DateTimeFormat(LOCALE_TAG[locale], short ? { month: 'short', year: '2-digit', timeZone: 'UTC' } : { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  };
}

/** A percentage with one decimal, or a dash when it is unknown — never 0%. */
export function usePercent() {
  const num = useNumber();
  return (value: number | null | undefined) => (value === null || value === undefined || !Number.isFinite(value) ? '—' : `${num(value, 1)}%`);
}

/** A range of company days, left to right in both languages. */
export function DayRange({ start, last, className }: { start: string; last: string; className?: string }) {
  return (
    <span dir="ltr" className={clsx('tabular whitespace-nowrap', className)}>
      {start === last ? start : `${start} → ${last}`}
    </span>
  );
}

/** Amounts in their own currencies, one under the other — never added across currencies. */
export function CurrencyAmounts({ amounts, empty }: { amounts: Array<{ currency: string; amount: number | string }>; empty?: ReactNode }) {
  const money = useMoney();
  const shown = amounts.filter((a) => Number(a.amount) !== 0);
  if (!shown.length) return <>{empty ?? '—'}</>;
  return (
    <span className="inline-flex flex-col tabular">
      {shown.map((a) => (
        <span key={a.currency}>{money(Number(a.amount), a.currency)}</span>
      ))}
    </span>
  );
}

/** A short label with its value, for the small facts under a heading. */
export function Fact({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={clsx('min-w-0', className)}>
      <dt className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{label}</dt>
      <dd className="mt-0.5 text-body-sm text-[var(--text-primary)] break-words">{children}</dd>
    </div>
  );
}

/** "Not guaranteed" — on every figure of money that may or may not arrive. */
export function NotGuaranteed() {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center rounded-pill bg-warning-bg px-2 py-0.5 text-caption font-medium text-warning-fg ring-1 ring-inset ring-warning/30 dark:bg-warning/14 dark:text-warning-dark">
      {t('finance.expected.notGuaranteed')}
    </span>
  );
}
